import { NodesMap } from "../../../lib/file-manager";
import { apiClient } from "../../../utils/apiClient";
import type { FolderResponseDto, FileResponseDto } from "@secure-caseworker/shared";

const getDocumentNodes = async (caseId: string) => {
  // Get all folders for the case
  const allFoldersResponse = await apiClient.getAllFolders(caseId);

  // Get root-level files (files not in any folder)
  const filesResponse = await apiClient.listFilesByCase(caseId);

  // Convert API response to FileNode format
  const nodesMap: NodesMap = {};

  // Add all folders to the map (both root and nested)
  if (allFoldersResponse && Array.isArray(allFoldersResponse)) {
    allFoldersResponse.forEach((folder: FolderResponseDto) => {
      nodesMap[folder.id] = {
        id: folder.id,
        name: folder.name,
        type: "folder",
        parentId: folder.parentId || null,
        createdAt: folder.createdAt || new Date().toISOString(),
        updatedAt: folder.updatedAt || new Date().toISOString(),
        clientPermission: folder.clientPermission || "none",
      };
    });
  }

  // Load contents for each folder to get files with correct parentId
  const folderContentsPromises = allFoldersResponse.map(
    (folder: FolderResponseDto) =>
      apiClient.getFolderContents(folder.id).then((contents) => ({
        folderId: folder.id,
        contents,
      }))
  );

  const allFolderContents = await Promise.all(folderContentsPromises);

  // Track which files are in folders
  const filesInFolders = new Set<string>();

  // Add files from folder contents
  allFolderContents.forEach(({ folderId, contents }) => {
    if (Array.isArray(contents)) {
      contents.forEach((folderItem) => {
        const item = folderItem.item;
        if (!item) return;

        if (folderItem.itemType === "file") {
          const fileId = item.id || item.fileId;
          filesInFolders.add(fileId);

          const fileName =
            item.filename ||
            item.original_filename ||
            item.name ||
            "Unnamed File";
          const nameParts = fileName.split(".");
          const ext =
            nameParts.length > 1 ? nameParts[nameParts.length - 1] : undefined;

          nodesMap[fileId] = {
            id: fileId,
            name: fileName,
            type: "file",
            parentId: folderId,
            size: item.size || item.size_bytes || item.fileSize,
            ext,
            mimeType: item.mime_type || item.mimeType,
            createdAt:
              item.created_at ||
              item.createdAt ||
              item.uploadedAt ||
              new Date().toISOString(),
            updatedAt:
              item.updated_at || item.updatedAt || new Date().toISOString(),
          };
        }
      });
    }
  });

  // Add root-level files (files not in any folder)
  if (filesResponse && Array.isArray(filesResponse)) {
    filesResponse.forEach((file: FileResponseDto) => {
      const fileId = file.id;

      // Skip files that are already in folders
      if (filesInFolders.has(fileId)) {
        return;
      }

      const fileName = file.filename || "Unnamed File";
      const fileSize = file.size;
      const mimeType = file.mimeType;
      const createdAt = file.uploadedAt;

      // Extract file extension from filename
      const nameParts = fileName.split(".");
      const ext =
        nameParts.length > 1 ? nameParts[nameParts.length - 1] : undefined;

      nodesMap[fileId] = {
        id: fileId,
        name: fileName,
        type: "file",
        parentId: null, // Root level files
        size: fileSize,
        ext,
        mimeType,
        createdAt: createdAt || new Date().toISOString(),
        updatedAt: createdAt || new Date().toISOString(),
      };
    });
  }

  return nodesMap;
};

export default getDocumentNodes;
